// src/hooks/useCalendarEvents.ts
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import * as Sentry from '@sentry/react';
import { supabase } from '@/lib/supabaseClient';
import type {
  RawAppointment,
  DiaAusencia,
  CalendarEvent,
  Horario,
} from '@/components/types/typeCalendar';
import {
  mapearCitas,
  buildEventosAusencias,
  buildEventosFestivos,
  buildEventosNoLaborables,
} from '@/components/utils/calendarUtils';

/* ============================================================
   📌 TRABAJADOR
============================================================ */
export function useCalendarEventsPorTrabajador(
  idUsuario: string,
  idEmpresa: string,
  fechaInicio: string | null,
  fechaFin: string | null,
  diasNoLaborables: string[],
  diasFestivos: string[],
  ausencias: DiaAusencia[],
  reloadKey: number,
  horariosUsuario: Horario[]
) {
  const [rawCitas, setRawCitas] = useState<RawAppointment[]>([]);
  const [citasEvt, setCitasEvt] = useState<CalendarEvent[]>([]);

  const fetchCitasRef = useRef<() => void>(() => {});

  // Cambiar de rango deja peticiones en vuelo. Sin este contador, la
  // respuesta del rango anterior podía llegar la última y machacar la buena:
  // el calendario mostraba el día nuevo pero con las citas del viejo, o sea
  // vacío, hasta pulsar actualizar.
  const peticion = useRef(0);

  const fetchCitas = useCallback(async () => {
    if (
      !idUsuario ||
      idUsuario === 'skip' ||
      !idEmpresa ||
      !fechaInicio ||
      !fechaFin
    ) {
      return;
    }

    const miPeticion = ++peticion.current;

    const filtroRango = [
      `and(fecha_cita.gte.${fechaInicio},fecha_cita.lte.${fechaFin})`,
      `and(fecha_fin.gte.${fechaInicio},fecha_fin.lte.${fechaFin})`,
      `and(fecha_cita.lte.${fechaInicio},fecha_fin.gte.${fechaFin})`,
    ].join(',');

    const { data, error } = await supabase
      .from('vista_citas_detalle')
      .select('*')
      .contains(
        'trabajadores_en_cita',
        JSON.stringify([{ id_usuario: idUsuario }])
      )
      .eq('id_empresa', idEmpresa)
      .or(filtroRango);

    if (miPeticion !== peticion.current) return;

    if (error) {
      Sentry.captureException(error, {
        extra: { idUsuario, idEmpresa, fechaInicio, fechaFin },
      });
      return;
    }

    setRawCitas((data ?? []) as RawAppointment[]);
  }, [idUsuario, idEmpresa, fechaInicio, fechaFin]);

  useEffect(() => {
    fetchCitasRef.current = fetchCitas;
  }, [fetchCitas]);

  useEffect(() => {
    fetchCitas();
  }, [fetchCitas, reloadKey]);

  useEffect(() => {
    if (!rawCitas.length) {
      setCitasEvt([]);
      return;
    }
    const mapped = mapearCitas(rawCitas, horariosUsuario || []);
    setCitasEvt(mapped);
  }, [rawCitas, horariosUsuario]);

  useEffect(() => {
    if (!idEmpresa || !idUsuario || idUsuario === 'skip') return;

    const channelName = `citas-rt-trabajador-${idUsuario}-${idEmpresa}`;
    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'citas' },
        () => {
          fetchCitasRef.current?.();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [idUsuario, idEmpresa]);

  const noLabEvt = useMemo(
    () => buildEventosNoLaborables(diasNoLaborables),
    [diasNoLaborables]
  );
  const festivosEvt = useMemo(
    () => buildEventosFestivos(diasFestivos),
    [diasFestivos]
  );
  const ausenciasEvt = useMemo(
    () => buildEventosAusencias(ausencias),
    [ausencias]
  );

  return useMemo(
    () => [...noLabEvt, ...ausenciasEvt, ...festivosEvt, ...citasEvt],
    [noLabEvt, ausenciasEvt, festivosEvt, citasEvt]
  );
}

/* ============================================================
   🏢 EMPRESA
============================================================ */
export function useCalendarEventsPorEmpresa(
  idEmpresa: string,
  fechaInicio: string | null,
  fechaFin: string | null,
  diasNoLaborables: string[],
  diasFestivos: string[],
  reloadKey: number,
  horariosEmpresa: Horario[]
) {
  const [rawCitas, setRawCitas] = useState<RawAppointment[]>([]);
  const [citasEvt, setCitasEvt] = useState<CalendarEvent[]>([]);

  const fetchCitasRef = useRef<() => void>(() => {});

  // Mismo motivo que en el hook del trabajador: descartar las respuestas
  // de un rango que ya no es el visible.
  const peticion = useRef(0);

  const fetchCitas = useCallback(async () => {
    if (!idEmpresa || idEmpresa === 'skip' || !fechaInicio || !fechaFin) return;

    const miPeticion = ++peticion.current;

    const filtroRango = [
      `and(fecha_cita.gte.${fechaInicio},fecha_cita.lte.${fechaFin})`,
      `and(fecha_fin.gte.${fechaInicio},fecha_fin.lte.${fechaFin})`,
      `and(fecha_cita.lte.${fechaInicio},fecha_fin.gte.${fechaFin})`,
    ].join(',');

    const { data, error } = await supabase
      .from('vista_citas_detalle')
      .select('*')
      .eq('id_empresa', idEmpresa)
      .or(filtroRango);

    if (miPeticion !== peticion.current) return;

    if (error) {
      Sentry.captureException(error, {
        extra: { idEmpresa, fechaInicio, fechaFin },
      });
      return;
    }

    setRawCitas((data ?? []) as RawAppointment[]);
  }, [idEmpresa, fechaInicio, fechaFin]);

  useEffect(() => {
    fetchCitasRef.current = fetchCitas;
  }, [fetchCitas]);

  useEffect(() => {
    fetchCitas();
  }, [fetchCitas, reloadKey]);

  useEffect(() => {
    if (!rawCitas.length) {
      setCitasEvt([]);
      return;
    }
    const mapped = mapearCitas(rawCitas, horariosEmpresa || []);
    setCitasEvt(mapped);
  }, [rawCitas, horariosEmpresa]);

  useEffect(() => {
    if (!idEmpresa || idEmpresa === 'skip') return;

    const channelName = `citas-rt-empresa-${idEmpresa}`;
    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'citas' },
        () => {
          fetchCitasRef.current?.();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [idEmpresa]);

  const noLabEvt = useMemo(
    () => buildEventosNoLaborables(diasNoLaborables),
    [diasNoLaborables]
  );
  const festivosEvt = useMemo(
    () => buildEventosFestivos(diasFestivos),
    [diasFestivos]
  );

  return useMemo(
    () => [...noLabEvt, ...festivosEvt, ...citasEvt],
    [noLabEvt, festivosEvt, citasEvt]
  );
}